import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import slugify from "slugify";
import { createNewsUpdates, NewsUpdatesData } from "../../../services/newsupdates";
import { getMedia, uploadMedia } from "../../../services/admin/media";
import RichTextEditor from "../../../Component/RickTextEditor";

function AddAdminNewsUpdates() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState<NewsUpdatesData>({
        title: "",
        slug: "",
        content: "",
        image: "",
        status: "publish",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Modal
    const [showModal, setShowModal] = useState(false);
    const [mediaList, setMediaList] = useState<{ id: number; name: string; url: string }[]>([]);
    const [uploading, setUploading] = useState(false);
    const [visibleMedia, setVisibleMedia] = useState<{ id: number; name: string; url: string }[]>([]);
    const [page, setPage] = useState(1);
    const limit = 24; // images per page

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        let updatedSlug = formData.slug;
        if (name === "title") {
            updatedSlug = slugify(value, { lower: true, strict: true });
        }
        setFormData({
            ...formData,
            [name]: value,
            slug: name === "title" ? updatedSlug : formData.slug,
        });
    };

    const handleContentChange = (content: string) => {
        setFormData((prev) => ({ ...prev, content }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        const res = await createNewsUpdates(formData);
        if (res.success) {
            navigate("/admin/news-updates");
        } else {
            setError(res.error);
        }
        setLoading(false);
    };

    const fetchMedia = async () => {
        const res = await getMedia();
        if (res.success) {
            const formatted = res.data.map((m: any) => ({
                id: m.id,
                name: m.name || m.filename || m.original_name || m.url.split("/").pop(),
                url: m.url,
            }));
            const reversed = [...formatted].reverse();
            setMediaList(reversed);
            setVisibleMedia(reversed.slice(0, limit));
        }
    };

    useEffect(() => {
        if (page > 1) {
            setVisibleMedia(mediaList.slice(0, page * limit));
        }
    }, [page, mediaList]);

    useEffect(() => {
        if (showModal) fetchMedia();
    }, [showModal]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;
        const fileList = Array.from(files);
        setUploading(true);
        const res = await uploadMedia(fileList);
        if (res.success) {
            fetchMedia();
        }
        setUploading(false);
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-semibold text-gray-800">Add News & Updates</h1>
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-sm text-gray-600 hover:text-[#009FE3]"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back
                </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row gap-6">
                {/* Left Main Form */}
                <div className="flex-1 space-y-4">
                    {error && <div className="text-sm text-red-600 bg-red-100 p-2 rounded">{error}</div>}

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009FE3]"
                        />
                    </div>

                    {/* Slug */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                        <input
                            type="text"
                            name="slug"
                            value={formData.slug}
                            readOnly
                            className="w-full px-4 py-2 border bg-gray-100 rounded-lg"
                        />
                    </div>

                    {/* Content Editor */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                        <RichTextEditor
                            initialData={formData.content}
                            isInvalid={false}
                            initialLoading={false}
                            onChange={(data: string) => handleContentChange(data)}
                        />
                    </div>
                </div>

                {/* Right Sidebar */}
                <div className="w-full lg:w-1/4 space-y-4">
                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009FE3]"
                        >
                            <option value="publish">Publish</option>
                            <option value="draft">Draft</option>
                        </select>
                    </div>

                    {/* Image */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                        <button
                            type="button"
                            onClick={() => setShowModal(true)}
                            className="w-full px-4 py-2 bg-gray-100 border rounded-lg hover:bg-gray-200"
                        >
                            {formData.image ? "Change Image" : "Select Image"}
                        </button>
                        {formData.image && (
                            <img
                                src={formData.image} // already full URL
                                alt="Selected"
                                className="mt-2 w-full border rounded"
                            />
                        )}
                    </div>

                    {/* Submit Button in Sidebar */}
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-2 px-4 text-white rounded-lg transition ${
                            loading ? "bg-[#009FE3]/50 cursor-not-allowed" : "bg-[#009FE3] hover:bg-[#008AC6]"
                        }`}
                    >
                        {loading ? "Adding..." : "Add News"}
                    </button>
                </div>
            </form>

            {/* Media Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg h-[90%] w-[90%] relative">
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute top-3 right-3 text-gray-500 hover:text-black"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <h2 className="text-lg font-semibold mb-4">Select Image</h2>

                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="mb-4"
                        />
                        {uploading && <p className="text-sm text-gray-500 mb-2">Uploading...</p>}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4 max-h-[75%] overflow-y-auto">
                            {visibleMedia
                                .filter((media) => /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(media.url))
                                .map((media) => (
                                    <div
                                        key={media.id}
                                        className="border rounded hover:shadow cursor-pointer"
                                        onClick={() => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                image: `${media.url}`,
                                            }));
                                            setShowModal(false);
                                        }}
                                    >
                                        <img
                                            src={`${media.url}`}
                                            alt={media.name}
                                            className="w-full h-28 object-cover rounded"
                                        />
                                        <p
                                            className="text-sm text-center truncate p-2 border-t border-[#f5f5f5] bg-white"
                                            title={media.name}
                                        >
                                            {media.name}
                                        </p>
                                    </div>
                                ))}
                        </div>
                        {/* Load More Button */}
                        {visibleMedia.length < mediaList.length && (
                            <div className="flex justify-center mt-4">
                                <button
                                    className="px-4 py-2 bg-[#009FE3] text-white rounded hover:bg-[#0085c5]"
                                    onClick={() => setPage((prev) => prev + 1)}
                                >
                                    Load More
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AddAdminNewsUpdates;
